/** @jsxImportSource theme-ui */
import { uniqBy } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { Card, Grid } from 'semantic-ui-react';

import * as dto from 'client/dto';
import { sxtheme } from 'utils/cssHelpers';
import { formatDateISO } from 'utils/dateTimeHelpers';
import { IsLocationProximalToUserLocations } from 'utils/modelHelpers';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { ReferenceListItem } from 'components/SidebarView/EventDetailPanel/ReferenceList';
import { TextTruncate } from 'components/TextTruncate';

type LabelStat = {
  label: string;
  stat: number;
  sources?: dto.ArticleModel[];
};

type DiseaseActivityUpdateCardProps = {
  reportDate: string;
  countryDescriptions?: dto.CountryDescriptionListModel[];
  totalSourceCount?: number;
  statLHS?: LabelStat;
  statRHS?: LabelStat;
  userLocationIds?: number[];
  isActive: boolean;
  isScrollToTarget: boolean;
};

export const DiseaseActivityUpdateCard: React.FC<DiseaseActivityUpdateCardProps> = ({
  reportDate,
  countryDescriptions,
  totalSourceCount,
  statLHS,
  statRHS,
  userLocationIds = [],
  isActive = false,
  isScrollToTarget = false,
}) => {
  const ref = React.useRef<HTMLDivElement>();

  const descriptionsAggregate = useMemo<dto.DescriptionReportModel[]>(
    () => (countryDescriptions || []).reduce((acc, x) => [...acc, ...x.descriptionList], []),
    [countryDescriptions]
  );
  const caseReportArticlesAggregate = useMemo<dto.ArticleModel[]>(() => {
    const agg = uniqBy(
      ((statLHS || ({} as LabelStat))?.sources ?? []).concat(
        (statRHS || ({} as LabelStat))?.sources ?? []
      ),
      'id'
    );

    console.log(statLHS);
    console.log(statRHS);
    console.log(`article ids: ${agg.map(x => x.id)}`);
    return agg;
  }, [statLHS, statRHS]);
  const articlesAggregate = useMemo<dto.ArticleModel[]>(
    () =>
      uniqBy(
        (descriptionsAggregate || [])
          .reduce((acc, x) => acc.concat(x.articles || []), [])
          .concat(caseReportArticlesAggregate),
        'id'
      ),
    [descriptionsAggregate, caseReportArticlesAggregate]
  );
  const additionalSourceCount = useMemo<number>(
    () => Math.max(0, (totalSourceCount ?? 0) - articlesAggregate.length),
    [totalSourceCount, articlesAggregate]
  );

  useEffect(() => {
    if (isScrollToTarget && ref && ref.current && ref.current.scrollIntoView)
      ref.current.scrollIntoView({ block: 'nearest' });
  }, [ref, isScrollToTarget]);

  const locationNameColor = (locationId: number, provinceId: number, countryId: number) =>
    IsLocationProximalToUserLocations(
      {
        locationId,
        provinceId,
        countryId,
      },
      userLocationIds
    )
      ? 'seafoam100'
      : 'deepSea90';

  return (
    <div
      ref={ref}
      data-testid="DiseaseActivityUpdateCard"
      className="ui fluid card"
      sx={{
        '&.ui.card': {
          ...(isActive && {
            border: `3px solid #C8D5E7`,
            margin: `-2px`,
            boxSizing: `content-box`,
          }),
        },
      }}
    >
      <Card.Content>
        <FlexGroup
          prefix={<BdIcon name="icon-calendar" color="stone50" />}
          alignItems="center"
          sx={{
            mb: '8px',
          }}
        >
          <Typography variant="h6" color="stone90" data-testid="reportDate">
            {formatDateISO(reportDate)}
          </Typography>
        </FlexGroup>
        <Grid columns="equal" divided="vertically">
          <Grid.Row divided>
            {statLHS && (
              <Grid.Column>
                <Typography variant="caption" color="deepSea50">
                  <span sx={{ verticalAlign: 'middle', mr: '2px' }}>{statLHS.label}</span>
                </Typography>
                <Typography variant="subtitle2" color="stone90">
                  {formatNumber(statLHS.stat, null, null, 'emdash-0')}
                </Typography>
              </Grid.Column>
            )}
            {statRHS && (
              <Grid.Column>
                <Typography variant="caption" color="deepSea50">
                  <span sx={{ verticalAlign: 'middle', mr: '2px' }}>{statRHS.label}</span>
                </Typography>
                <Typography variant="subtitle2" color="stone90">
                  {formatNumber(statRHS.stat, null, null, 'emdash-0')}
                </Typography>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
        {descriptionsAggregate?.length > 0 && (
          <div
            sx={{
              mt: '16px',
              pt: '24px',
              mb: '-4px',
              borderTop: sxtheme(t => `1px solid ${t.colors.stone20}`),
            }}
          >
            {descriptionsAggregate.map(
              ({ locationId, provinceId, countryId, locationName, description }) => (
                <div key={locationName} sx={{ mb: '20px' }}>
                  <Typography
                    variant="h6"
                    color={locationNameColor(locationId, provinceId, countryId)}
                    marginBottom="4px"
                  >
                    {locationName}
                  </Typography>
                  <Typography variant="body2" color="stone90" marginBottom="4px">
                    <TextTruncate value={description} length={150} isExpanded={isActive} />
                  </Typography>
                </div>
              )
            )}
          </div>
        )}
        {(!!articlesAggregate?.length || !!additionalSourceCount) && (
          <>
            <Typography variant="overline" color="deepSea90" sx={{ mb: '6px' }}>
              Sources
            </Typography>
            {articlesAggregate.map(article => (
              <div sx={{ mb: '10px' }}>
                <ReferenceListItem {...article} />
              </div>
            ))}
            {!!additionalSourceCount && (
              <Typography
                data-testid="update-additional-sources"
                variant="caption"
                color="deepSea70"
                sx={{ mb: '6px' }}
              >
                {articlesAggregate?.length ? '+ ' : ''}
                {formatNumber(additionalSourceCount, 'source', 'sources', false)} for global case
                data. Please view individual country activity for all sources.
              </Typography>
            )}
          </>
        )}
      </Card.Content>
    </div>
  );
};

export default DiseaseActivityUpdateCard;
